import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Students API
export const studentsApi = {
  getAll: async (params = {}) => {
    const { page = 1, per_page = 20, search, status, level, visa_objective } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('per_page', per_page);
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('status', status);
    if (level) queryParams.append('level', level);
    if (visa_objective) queryParams.append('visa_objective', visa_objective);
    
    const response = await axios.get(`${API_URL}/students?${queryParams.toString()}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/students/${id}`);
    return response.data;
  },
  
  getAuditLogs: async (id) => {
    const response = await axios.get(`${API_URL}/students/${id}/audit-logs`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/students`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/students/${id}`, data);
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await axios.patch(`${API_URL}/students/${id}/status`, { status });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/students/${id}`);
    return response.data;
  },
};

// Partners API (Enhanced)
export const partnersApi = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    if (params.status) queryParams.append('status', params.status);
    if (params.country) queryParams.append('country', params.country);
    queryParams.append('limit', params.limit || 100);
    const response = await axios.get(`${API_URL}/partners?${queryParams.toString()}`);
    return response.data;
  },
  
  getById: async (partnerId) => {
    const response = await axios.get(`${API_URL}/partners/${partnerId}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/partners`, data);
    return response.data;
  },
  
  update: async (partnerId, data) => {
    const response = await axios.put(`${API_URL}/partners/${partnerId}`, data);
    return response.data;
  },
  
  delete: async (partnerId) => {
    const response = await axios.delete(`${API_URL}/partners/${partnerId}`);
    return response.data;
  },
  
  // Get current partner's own profile
  getMyProfile: async () => {
    const response = await axios.get(`${API_URL}/partner/me`);
    return response.data;
  },
  
  // Candidates
  getCandidates: async (partnerId) => {
    const response = await axios.get(`${API_URL}/partners/${partnerId}/candidates`);
    return response.data;
  },
  
  assignCandidate: async (partnerId, studentId) => {
    const response = await axios.post(`${API_URL}/partners/${partnerId}/candidates/${studentId}`);
    return response.data;
  },
  
  updateCandidateStatus: async (partnerId, studentId, data) => {
    const response = await axios.patch(`${API_URL}/partners/${partnerId}/candidates/${studentId}`, data);
    return response.data;
  },
  
  removeCandidate: async (partnerId, studentId) => {
    const response = await axios.delete(`${API_URL}/partners/${partnerId}/candidates/${studentId}`);
    return response.data;
  },
  
  // Needs/Offers
  getNeeds: async (partnerId) => {
    const response = await axios.get(`${API_URL}/partners/${partnerId}/needs`);
    return response.data;
  },
  
  createNeed: async (partnerId, data) => {
    const response = await axios.post(`${API_URL}/partners/${partnerId}/needs`, data);
    return response.data;
  },
  
  updateNeed: async (partnerId, needId, data) => {
    const response = await axios.put(`${API_URL}/partners/${partnerId}/needs/${needId}`, data);
    return response.data;
  },
  
  deleteNeed: async (partnerId, needId) => {
    const response = await axios.delete(`${API_URL}/partners/${partnerId}/needs/${needId}`);
    return response.data;
  },
  
  // Integration Tracking
  getIntegration: async (partnerId, studentId) => {
    const response = await axios.get(`${API_URL}/partners/${partnerId}/integration/${studentId}`);
    return response.data;
  },
  
  updateIntegration: async (partnerId, studentId, data) => {
    const response = await axios.patch(`${API_URL}/partners/${partnerId}/integration/${studentId}`, data);
    return response.data;
  },
};

// Courses API
export const coursesApi = {
  getAll: async (params = {}) => {
    const { page = 1, per_page = 20, search, status, level, teacher_id } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('per_page', per_page);
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('status', status);
    if (level) queryParams.append('level', level);
    if (teacher_id) queryParams.append('teacher_id', teacher_id);
    
    const response = await axios.get(`${API_URL}/courses?${queryParams.toString()}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/courses/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/courses`, data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await axios.put(`${API_URL}/courses/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/courses/${id}`);
    return response.data;
  },
  
  getEnrolledStudents: async (courseId) => {
    const response = await axios.get(`${API_URL}/courses/${courseId}/enrolled-students`);
    return response.data;
  },
  
  enrollStudent: async (courseId, studentId) => {
    const response = await axios.post(`${API_URL}/courses/${courseId}/enroll`, { student_id: studentId, course_id: courseId });
    return response.data;
  },
  
  unenrollStudent: async (courseId, studentId) => {
    const response = await axios.delete(`${API_URL}/courses/${courseId}/enroll/${studentId}`);
    return response.data;
  },
};

// Sessions API
export const sessionsApi = {
  getByCourse: async (courseId) => {
    const response = await axios.get(`${API_URL}/sessions?course_id=${courseId}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await axios.get(`${API_URL}/sessions/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/sessions`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/sessions/${id}`);
    return response.data;
  },
  
  getAttendance: async (sessionId) => {
    const response = await axios.get(`${API_URL}/sessions/${sessionId}/attendance`);
    return response.data;
  },
  
  markAttendance: async (sessionId, attendances) => {
    const response = await axios.post(`${API_URL}/sessions/${sessionId}/attendance`, { attendances });
    return response.data;
  },
};

// Materials API
export const materialsApi = {
  getByCourse: async (courseId, sessionId = null) => {
    let url = `${API_URL}/materials?course_id=${courseId}`;
    if (sessionId) url += `&session_id=${sessionId}`;
    const response = await axios.get(url);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/materials`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/materials/${id}`);
    return response.data;
  },
};

// Grades API
export const gradesApi = {
  getByCourse: async (courseId, studentId = null) => {
    let url = `${API_URL}/grades?course_id=${courseId}`;
    if (studentId) url += `&student_id=${studentId}`;
    const response = await axios.get(url);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/grades`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/grades/${id}`);
    return response.data;
  },
};

// Certificates API
export const certificatesApi = {
  getAll: async (studentId = null, courseId = null) => {
    const params = new URLSearchParams();
    if (studentId) params.append('student_id', studentId);
    if (courseId) params.append('course_id', courseId);
    const response = await axios.get(`${API_URL}/certificates?${params.toString()}`);
    return response.data;
  },
  
  issue: async (data) => {
    const response = await axios.post(`${API_URL}/certificates`, data);
    return response.data;
  },
};

// Teacher API
export const teacherApi = {
  getStats: async () => {
    const response = await axios.get(`${API_URL}/teacher/stats`);
    return response.data;
  },
  
  getCourses: async () => {
    const response = await axios.get(`${API_URL}/teacher/courses`);
    return response.data;
  },
};

// Student API (for student-specific endpoints)
export const studentApi = {
  getMyCourses: async () => {
    const response = await axios.get(`${API_URL}/student/my-courses`);
    return response.data;
  },
  
  getProgression: async () => {
    const response = await axios.get(`${API_URL}/student/progression`);
    return response.data;
  },
};

// Users API (for teachers dropdown)
export const usersApi = {
  getTeachers: async () => {
    const response = await axios.get(`${API_URL}/users?role=teacher`);
    return response.data;
  },
};

// ==================== DOSSIERS API ====================

export const dossiersApi = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.search) queryParams.append('search', params.search);
    if (params.visa_objective) queryParams.append('visa_objective', params.visa_objective);
    if (params.status) queryParams.append('status', params.status);
    const response = await axios.get(`${API_URL}/dossiers?${queryParams.toString()}`);
    return response.data;
  },
  
  getDetail: async (studentId) => {
    const response = await axios.get(`${API_URL}/dossiers/${studentId}`);
    return response.data;
  },
  
  generatePdf: async (studentId) => {
    const response = await axios.post(`${API_URL}/dossiers/${studentId}/generate-pdf`);
    return response.data;
  },
  
  getAuditLogs: async (studentId) => {
    const response = await axios.get(`${API_URL}/dossiers/${studentId}/audit-logs`);
    return response.data;
  },
};

// Documents API
export const documentsApi = {
  getByStudent: async (studentId) => {
    const response = await axios.get(`${API_URL}/documents?student_id=${studentId}`);
    return response.data;
  },
  
  upload: async (studentId, documentType, file) => {
    const formData = new FormData();
    formData.append('student_id', studentId);
    formData.append('document_type', documentType);
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  review: async (documentId, status, comment = null) => {
    const response = await axios.put(`${API_URL}/documents/${documentId}/review`, {
      status,
      comment,
    });
    return response.data;
  },
  
  delete: async (documentId) => {
    const response = await axios.delete(`${API_URL}/documents/${documentId}`);
    return response.data;
  },
  
  getFileUrl: (documentId) => {
    return `${API_URL}/documents/${documentId}/file`;
  },
};

// Appointments API
export const appointmentsApi = {
  getByStudent: async (studentId) => {
    const response = await axios.get(`${API_URL}/appointments?student_id=${studentId}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await axios.post(`${API_URL}/appointments`, data);
    return response.data;
  },
  
  update: async (appointmentId, data) => {
    const response = await axios.put(`${API_URL}/appointments/${appointmentId}`, data);
    return response.data;
  },
  
  delete: async (appointmentId) => {
    const response = await axios.delete(`${API_URL}/appointments/${appointmentId}`);
    return response.data;
  },
};

// ==================== PAYMENTS API ====================

export const paymentsApi = {
  getStats: async () => {
    const response = await axios.get(`${API_URL}/payments/stats`);
    return response.data;
  },
  
  getPlans: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.student_id) queryParams.append('student_id', params.student_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    const response = await axios.get(`${API_URL}/payments/plans?${queryParams.toString()}`);
    return response.data;
  },
  
  getPlan: async (planId) => {
    const response = await axios.get(`${API_URL}/payments/plans/${planId}`);
    return response.data;
  },
  
  createPlan: async (data) => {
    const response = await axios.post(`${API_URL}/payments/plans`, data);
    return response.data;
  },
  
  deletePlan: async (planId) => {
    const response = await axios.delete(`${API_URL}/payments/plans/${planId}`);
    return response.data;
  },
  
  getInstallments: async (planId) => {
    const response = await axios.get(`${API_URL}/payments/plans/${planId}/installments`);
    return response.data;
  },
  
  recordPayment: async (installmentId, data) => {
    const response = await axios.post(`${API_URL}/payments/installments/${installmentId}/pay`, data);
    return response.data;
  },
  
  cancelPayment: async (installmentId) => {
    const response = await axios.delete(`${API_URL}/payments/installments/${installmentId}/cancel`);
    return response.data;
  },
  
  getStudentSummary: async (studentId) => {
    const response = await axios.get(`${API_URL}/payments/student/${studentId}/summary`);
    return response.data;
  },
};

// ==================== MESSAGING API ====================

export const messagingApi = {
  getUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/conversations/unread-count`);
    return response.data;
  },
  
  getConversations: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await axios.get(`${API_URL}/conversations${params}`);
    return response.data;
  },
  
  getConversation: async (conversationId) => {
    const response = await axios.get(`${API_URL}/conversations/${conversationId}`);
    return response.data;
  },
  
  createConversation: async (data) => {
    const response = await axios.post(`${API_URL}/conversations`, data);
    return response.data;
  },
  
  getMessages: async (conversationId, page = 1, limit = 30) => {
    const response = await axios.get(`${API_URL}/conversations/${conversationId}/messages?page=${page}&limit=${limit}`);
    return response.data;
  },
  
  sendMessage: async (conversationId, content, file = null) => {
    const formData = new FormData();
    if (content) formData.append('content', content);
    if (file) formData.append('file', file);
    
    const response = await axios.post(`${API_URL}/conversations/${conversationId}/messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  deleteMessage: async (conversationId, messageId) => {
    const response = await axios.delete(`${API_URL}/conversations/${conversationId}/messages/${messageId}`);
    return response.data;
  },
  
  getMessageableUsers: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await axios.get(`${API_URL}/users/messageable${params}`);
    return response.data;
  },
  
  getFileUrl: (filename) => {
    return `${API_URL}/files/messages/${filename}`;
  },
};

// ==================== NOTIFICATIONS API ====================

export const notificationsApi = {
  getUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/notifications/unread-count`);
    return response.data;
  },
  
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.read !== undefined) queryParams.append('read', params.read);
    if (params.category) queryParams.append('category', params.category);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const response = await axios.get(`${API_URL}/notifications?${queryParams.toString()}`);
    return response.data;
  },
  
  markAsRead: async (notificationId) => {
    const response = await axios.patch(`${API_URL}/notifications/${notificationId}/read`);
    return response.data;
  },
  
  markAllAsRead: async () => {
    const response = await axios.patch(`${API_URL}/notifications/read-all`);
    return response.data;
  },
  
  delete: async (notificationId) => {
    const response = await axios.delete(`${API_URL}/notifications/${notificationId}`);
    return response.data;
  },
  
  clearAll: async () => {
    const response = await axios.delete(`${API_URL}/notifications/clear-all`);
    return response.data;
  },
};

// ==================== STATISTICS API ====================

export const statsApi = {
  getGlobal: async () => {
    const response = await axios.get(`${API_URL}/stats/global`);
    return response.data;
  },
  
  getStudents: async () => {
    const response = await axios.get(`${API_URL}/stats/students`);
    return response.data;
  },
  
  getPayments: async () => {
    const response = await axios.get(`${API_URL}/stats/payments`);
    return response.data;
  },
  
  getDossiers: async () => {
    const response = await axios.get(`${API_URL}/stats/dossiers`);
    return response.data;
  },
  
  exportReport: async (type, format, filters = {}) => {
    const response = await axios.post(`${API_URL}/reports/export`, { type, format, filters });
    return response.data;
  },
  
  getReportDownloadUrl: (filename) => {
    return `${API_URL}/reports/download/${filename}`;
  },
};

// ==================== AVATAR API ====================

export const avatarApi = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/users/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  getUrl: (userId) => {
    return `${API_URL}/users/${userId}/avatar`;
  },
  
  delete: async () => {
    const response = await axios.delete(`${API_URL}/users/avatar`);
    return response.data;
  },
};

// ==================== CRON API ====================

export const cronApi = {
  runPaymentReminders: async () => {
    const response = await axios.post(`${API_URL}/cron/payment-reminders`);
    return response.data;
  },
};

// ==================== CONTACT API ====================

export const contactApi = {
  submit: async (data) => {
    const response = await axios.post(`${API_URL}/contact`, data);
    return response.data;
  },
  
  getMessages: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    queryParams.append('limit', params.limit || 50);
    const response = await axios.get(`${API_URL}/contact/messages?${queryParams.toString()}`);
    return response.data;
  },
  
  updateStatus: async (messageId, status) => {
    const response = await axios.patch(`${API_URL}/contact/messages/${messageId}?status=${status}`);
    return response.data;
  },
  
  delete: async (messageId) => {
    const response = await axios.delete(`${API_URL}/contact/messages/${messageId}`);
    return response.data;
  },
  
  getUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/contact/messages/count`);
    return response.data;
  },
};

// ==================== RESOURCES API ====================

export const resourcesApi = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.type) queryParams.append('type', params.type);
    if (params.category) queryParams.append('category', params.category);
    const response = await axios.get(`${API_URL}/resources?${queryParams.toString()}`);
    return response.data;
  },
  
  getFAQ: async () => {
    const response = await axios.get(`${API_URL}/resources/faq`);
    return response.data;
  },
  
  create: async (formData) => {
    const response = await axios.post(`${API_URL}/resources`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  update: async (resourceId, data) => {
    const response = await axios.put(`${API_URL}/resources/${resourceId}`, data);
    return response.data;
  },
  
  delete: async (resourceId) => {
    const response = await axios.delete(`${API_URL}/resources/${resourceId}`);
    return response.data;
  },
  
  getFileUrl: (resourceId) => {
    return `${API_URL}/resources/${resourceId}/file`;
  },
};

// ==================== LEADS API ====================

export const leadsApi = {
  // Public endpoint - no auth required
  create: async (data) => {
    const response = await axios.post(`${API_URL}/leads`, data);
    return response.data;
  },
  
  // Admin endpoints
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.source) queryParams.append('source', params.source);
    queryParams.append('limit', params.limit || 100);
    const response = await axios.get(`${API_URL}/leads?${queryParams.toString()}`);
    return response.data;
  },
  
  updateStatus: async (leadId, status) => {
    const response = await axios.patch(`${API_URL}/leads/${leadId}/status`, { status });
    return response.data;
  },
  
  delete: async (leadId) => {
    const response = await axios.delete(`${API_URL}/leads/${leadId}`);
    return response.data;
  },
};
